import { Config } from "@config";
import { splitEventParameters } from "@utils/google-analytics-4.fn";
import type { IntersectionOptions } from "react-intersection-observer";
import { Console } from "./console";
import type { EventData, GA4EventParameters } from "./google-analytics-4.type";
import { LocalStorageWithExpire } from "./local-storage-with-expire";
import { getUuid } from "./uuid";

export const GoogleAnalytics4 = (() => {
	let eventPool: EventData[] = [];
	let measurementId: string = undefined;

	function init(): void {
		if (window.dataLayer?.some((item) => item?.event === "gtag.load")) {
			startEventSending();
		} else {
			document.addEventListener("ga4_init", startEventSending);
		}
	}

	function startEventSending(): void {
		if (window?.dataLayer?.length > 0) {
			const loadEvent = window.dataLayer.find((item) => item?.event === "gtag.load");

			if (loadEvent?.measurementId) {
				measurementId = loadEvent?.measurementId;
				eventPool.forEach((item) => {
					sendEvent(item.event, item.eventModel);
				});
				eventPool = [];
			}
		}
	}

	function generateId(prefix: string): string {
		return `${prefix}-${getUuid()}`;
	}

	function getButtonId(storageName: string): string {
		return LocalStorageWithExpire.get(storageName);
	}

	function setButtonId(prefix: string, storageName: string): string {
		const id = generateId(prefix);

		LocalStorageWithExpire.set(storageName, id, 1 * 60 * 24); // 1 day

		return id;
	}

	function sendEvent(event: string, model?: GA4EventParameters): void {
		if (!measurementId) {
			eventPool.push({ event, eventModel: model });

			return;
		}

		const eventModel = {
			...model,
			send_to: measurementId,
			isSignedIn: Config.getCurrentUser().isSignedIn(),
			variant: Config.getAbVersion(),
		};

		const eventList = splitEventParameters(event, eventModel);

		eventList.forEach((item) => {
			window.gtag("event", item.event, item.eventModel);
			Console.log("GA4 event:", item.event, item.eventModel);
		});
	}

	function getInViewProps(threshold = 0.2, delay = 100): IntersectionOptions {
		return {
			threshold,
			delay,
		};
	}

	return {
		init,
		startEventSending,
		generateId,
		getButtonId,
		setButtonId,
		sendEvent,
		getInViewProps,
	};
})();
